import { useCallback } from 'react'

type FocusedIndex = { index: number; type: string; id: string } | null

export interface useSingleClickHandlingParams {
  gridOrder: string[]
  assetCountMap: Record<string, number>
  getIdMap: { [key: string]: Function }
  selectedAssetMap: Record<string, Set<any>>
  setSelectedAssetMap: Record<string, Function>
  focusedIndex: FocusedIndex
  setFocusedIndex: Function
}

export function useSingleClickHandling({
  gridOrder,
  assetCountMap,
  getIdMap,
  selectedAssetMap,
  setSelectedAssetMap,
  focusedIndex,
  setFocusedIndex,
}: useSingleClickHandlingParams) {
  return useCallback(
    (
      gridType: string,
      index: number,
      newId: string,
      event: React.MouseEvent<HTMLDivElement>,
    ) => {
      if (event.ctrlKey || event.metaKey) {
        // Toggle selection
        let isDeselected = false

        const newSet = selectedAssetMap[gridType]
        if (newSet.has(newId)) {
          newSet.delete(newId)
          isDeselected = true
        } else {
          newSet.add(newId)
        }
        setSelectedAssetMap[gridType](newSet)

        if (isDeselected) {
          setFocusedIndex(null)
        } else {
          setFocusedIndex({ index: index, type: gridType, id: newId })
        }
      } else if (event.shiftKey) {
        const oldGridIndex = gridOrder.indexOf(focusedIndex?.type || '')
        const newGridIndex = gridOrder.indexOf(gridType || '')

        if (
          oldGridIndex !== -1 &&
          newGridIndex !== -1 &&
          focusedIndex !== null
        ) {
          if (oldGridIndex === newGridIndex) {
            const newSet = selectedAssetMap[gridType]
            const start = Math.min(focusedIndex.index, index)
            const end = Math.max(focusedIndex.index, index)
            for (let i = start; i <= end; i++) {
              const id = getIdMap[gridType](i)
              if (id) newSet.add(id)
            }
            setSelectedAssetMap[gridType](newSet)
          } else if (oldGridIndex < newGridIndex) {
            for (let g = oldGridIndex; g <= newGridIndex; g++) {
              const intermediateGridType = gridOrder[g]
              if (g === oldGridIndex) {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = focusedIndex.index
                const end = assetCountMap[intermediateGridType] - 1
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              } else if (g === newGridIndex) {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = 0
                const end = index
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              } else {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = 0
                const end = assetCountMap[intermediateGridType] - 1
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              }
            }
          } else if (oldGridIndex > newGridIndex) {
            for (let g = newGridIndex; g <= oldGridIndex; g++) {
              const intermediateGridType = gridOrder[g]
              if (g === newGridIndex) {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = index
                const end = assetCountMap[intermediateGridType] - 1
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              } else if (g === oldGridIndex) {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = 0
                const end = focusedIndex.index
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              } else {
                const newSet = selectedAssetMap[intermediateGridType]
                const start = 0
                const end = assetCountMap[intermediateGridType] - 1
                for (let i = start; i <= end; i++) {
                  const id = getIdMap[intermediateGridType](i)
                  if (id) newSet.add(id)
                }
                setSelectedAssetMap[intermediateGridType](newSet)
              }
            }
          }
        } else {
          const newSet = selectedAssetMap[gridType]
          const id = getIdMap[gridType](index)
          if (id) newSet.add(id)
          setSelectedAssetMap[gridType](newSet)
        }

        setFocusedIndex({ index: index, type: gridType, id: newId })
      } else {
        selectedAssetMap[gridType] = new Set([newId])
        setSelectedAssetMap[gridType](selectedAssetMap[gridType])
        setFocusedIndex({ index: index, type: gridType, id: newId })
        Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
          if (type !== gridType) {
            selectedAssetMap[type].clear()
            selected(selectedAssetMap[type])
          }
        })
      }
    },
    [
      gridOrder,
      assetCountMap,
      getIdMap,
      selectedAssetMap,
      setSelectedAssetMap,
      focusedIndex,
      setFocusedIndex,
    ],
  )
}
